import { parseIngredientText } from './ingredientParser';

/**
 * "Is this stored ingredient row actually a section label?"
 *
 * Some publishers emit section labels inline in `recipeIngredient`, so they were
 * persisted as ingredients and the list rendered them as buyable rows — "En
 * accompagnement - Patates Deluxe" with a shopping cart beside it (#594).
 *
 * A label is PROMOTED to a header here, never deleted. It carries information
 * the cook needs ("Pour la sauce"), and a wrong guess then restyles a row
 * instead of removing an ingredient from the list — which is the failure mode
 * that matters (#574 deleted real ingredients this way).
 *
 * An audit of the bundled catalog (479 recipes, 5,875 ingredient lines) decided
 * the shape:
 *
 *   - Quantity-less is not a signal. 1,168 lines (19.9%) carry no quantity and
 *     nearly all are food — "Sel et poivre", "Citron", "Mayonnaise".
 *   - 16 lines carry an explicit marker: a trailing colon, `Pour la/le/les`,
 *     `For the`, `En accompagnement`.
 *   - Corroboration settles the ambiguous ones. `Sel et poivre :` ends with a
 *     colon and is food; every real header in the catalog is followed by a line
 *     carrying a quantity, and it is not.
 *
 * KNOWN LIMIT: 62 bare-noun labels ("Marinade", "Sauce", "Vinaigrette",
 * "Taboulé") are left as ingredient rows. Nothing in their shape separates them
 * from a one-word ingredient — the same list flagged "Sirop d'érable", which is
 * maple syrup. The extractor keeps its equivalent word-list signal demoted for
 * this reason (wrong ~2 times in 3, #574). Fixing those needs the page's own
 * markup or a classifier at import time, not a wider regex here.
 */

/** A label's opening words. Never how a food line starts. */
const LABEL_PREFIX = /^(?:pour\s+(?:la|le|les|l[’'])|for\s+the\b|en\s+accompagnement\b)/i;

/** An explicit "this is a label" marker. */
const EXPLICIT_MARKER = new RegExp(`(:\\s*$)|${LABEL_PREFIX.source}`, 'i');

function tidy(text: string): string {
  return text.replace(/[   ]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** ALL-CAPS with real letters in it — the shape the list already promoted. */
function isAllCapsLabel(line: string): boolean {
  const letters = line.replace(/[^a-zA-ZÀ-ÿ]/g, '');
  return letters.length > 3 && letters === letters.toUpperCase();
}

/**
 * @param text     the stored ingredient line
 * @param nextText the line beneath it, which is what corroborates a marker
 */
export function isInlineIngredientHeader(text: string, nextText?: string): boolean {
  const line = tidy(text || '');
  if (!line) return false;

  // A measured line is an ingredient, whatever else it looks like.
  if (parseIngredientText(line).numPart) return false;

  if (isAllCapsLabel(line)) return true;

  if (!EXPLICIT_MARKER.test(line)) return false;

  // Corroboration: a real section is followed by the ingredients that belong to
  // it. Without this, "Sel et poivre :" becomes a header and the seasoning
  // disappears from the list.
  if (!nextText) return false;
  return !!parseIngredientText(tidy(nextText)).numPart;
}

/**
 * "Should a pantry count skip this line?" — the matching question, not the
 * display one.
 *
 * The recipe index often stores a recipe's lines without their quantities, so
 * "for the lemon curd" sits above "lemons" with nothing to corroborate it, and
 * the list rightly keeps it as a row. But every ranking, and the planner's
 * pantry strip, read those same lines, and the vocabulary resolves "lemon
 * curd" to lemons: the header was counted as a use of the fridge. A label
 * prefix is enough here, because a wrong guess only drops one line from a
 * count; it never hides an ingredient from the cook.
 */
export function isLabelForMatching(text: string): boolean {
  const line = tidy(text || '');
  if (!line || parseIngredientText(line).numPart) return false;
  return isAllCapsLabel(line) || LABEL_PREFIX.test(line);
}
